import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {createDataCatalog, IDataCatalog, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, Spinner, TextInput} from '../Components';
import {AppState} from '../AppState';
import {Dialog} from './Dialog';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().trim(),
});

type SchemaType = z.infer<typeof schema>;

interface CreateDataCatalogDialogProps {
  onClose: () => void;
  onCreated: (catalog: IDataCatalog) => void;
}

export const CreateDataCatalogDialog: FC<CreateDataCatalogDialogProps> = ({onClose, onCreated}) => {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<SchemaType>({mode: 'all', resolver: zodResolver(schema), defaultValues: {name: '', description: ''}});

  const onSubmit = (data: SchemaType) => {
    // Tolerate double clicks
    if (submitting) {
      return;
    }

    setSubmitting(true);
    createDataCatalog(VerdocsEndpoint.getDefault(), AppState.currentProfile?.organization_id || '', {
      name: data.name,
      description: data.description || null,
    })
      .then((catalog) => {
        setSubmitting(false);
        onCreated(catalog);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating the catalog: ${e.response?.data?.message || e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="mb-4 text-lg font-medium text-[#33364b]">New data catalog</div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput label="Name" name="name" placeholder="Applicant fields" required />
          <TextInput label="Description" name="description" placeholder="What these keys describe" />

          <div className="mt-2 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
            {submitting && <Spinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button size="small" label="Create catalog" type="submit" disabled={submitting} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
